import { graphqlFetch } from '@pylot-data/graphqlFetch'
import { CART_QUERY_NAME } from '@pylot-data/core-queries/cart'
import { mutate, unstable_serialize } from 'swr'

const CREATE_CART_QUERY = /* GraphQL */ `
    mutation {
        createEmptyCart
    }
`

/**
 * Bandera heredada: antes se guardaba en localStorage mientras se creaba el
 * carro. Si la request fallaba o se cerraba la pestaña a mitad, quedaba puesta
 * para siempre y ese navegador nunca volvía a crear carro. Ya no se escribe;
 * solo se limpia si quedó de una versión anterior.
 */
export const CREATING_CART_KEY = 'creating_cart'

type CreateCartOptions = {
    locale?: string
}

/** Creación en curso, compartida para que dos clicks seguidos creen un solo carro. */
let pendingCart: Promise<string | undefined> | null = null

const requestEmptyCart = async (
    locale: string
): Promise<string | undefined> => {
    window.localStorage.removeItem(CREATING_CART_KEY)
    const res = await graphqlFetch<null, { createEmptyCart: string }>({
        query: CREATE_CART_QUERY,
        locale
    })
    const cartId = res?.data?.createEmptyCart
    if (!cartId) return undefined
    window.localStorage.setItem('cart_id', cartId)
    return cartId
}

/**
 * Devuelve el carro de invitado, creándolo solo si todavía no existe.
 *
 * El carro se crea de forma diferida, al agregar el primer producto, y no al
 * cargar la página: cada visita de un navegador nuevo (incluidos los bots que
 * renderizan la PWA) creaba un quote vacío que nunca se usaba.
 *
 * Con sesión iniciada devuelve undefined: Magento resuelve el carro del
 * cliente por el token y no hace falta crear uno.
 */
export const ensureCart = async ({
    locale = ''
}: CreateCartOptions = {}): Promise<string | undefined> => {
    if (typeof window === 'undefined') return undefined
    if (window.localStorage.getItem('pylot_token')) return undefined

    const savedCartId = window.localStorage.getItem('cart_id')
    if (savedCartId) return savedCartId

    if (!pendingCart) {
        pendingCart = requestEmptyCart(locale).finally(() => {
            pendingCart = null
        })
    }
    return pendingCart
}

/**
 * Crea el carro de invitado si falta y refresca useCart. Se usa donde el carro
 * acaba de descartarse (logout, pedido pagado, token vencido); el flujo normal
 * de la tienda usa ensureCart al agregar el primer producto.
 */
export const createCart = async ({
    locale = ''
}: CreateCartOptions = {}): Promise<string | undefined> => {
    const isClient = typeof window !== 'undefined'
    const isSavedCart = isClient && window.localStorage.getItem('cart_id')
    if (!isClient || isSavedCart) return undefined

    const cartId = await ensureCart({ locale })
    if (!cartId) return undefined

    // La key real de useCart es el array [CART_QUERY_NAME] (ver use-query.tsx),
    // así que usamos unstable_serialize para calcular la misma key interna de SWR.
    mutate(unstable_serialize([CART_QUERY_NAME]), null)

    return cartId
}
